import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Phone, Lock, ArrowRight, Eye, EyeOff, CheckCircle } from 'lucide-react';

export default function Register() {
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const pinMatch = pin.length === 4 && confirmPin.length === 4 && pin === confirmPin;
  const pinMismatch = confirmPin.length === 4 && pin !== confirmPin;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pin !== confirmPin) {
      setError('PINs do not match. Please check and try again.');
      return;
    }
    if (pin.length !== 4) {
      setError('PIN must be exactly 4 digits.');
      return;
    }
    try {
      setError('');
      setLoading(true);
      await register(phone, pin);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — branding (hidden on mobile) */}
      <div
        className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1565c0 0%, #1e90ff 50%, #42a5f5 100%)',
        }}
      >
        {/* Decorative blobs */}
        <div className="absolute top-[-80px] right-[-80px] w-80 h-80 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, #fff 0%, transparent 70%)' }} />
        <div className="absolute bottom-[-60px] left-[-60px] w-64 h-64 rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, #fff 0%, transparent 70%)' }} />

        {/* Logo */}
        <div className="flex items-center gap-3 relative z-10">
          <div className="w-11 h-11 bg-white/20 rounded-2xl flex items-center justify-center text-white font-extrabold text-xl backdrop-blur-sm">
            K
          </div>
          <span className="text-white font-extrabold text-2xl tracking-tight">Katabo</span>
        </div>

        {/* Hero text */}
        <div className="relative z-10">
          <h1 className="text-4xl font-extrabold text-white leading-tight mb-4">
            Start your financial<br />journey today.
          </h1>
          <p className="text-blue-100 text-lg leading-relaxed max-w-sm">
            Join thousands tracking their budgets and savings smarter with Katabo.
          </p>

          {/* Steps */}
          <div className="mt-8 space-y-3">
            {[
              'Create your account in seconds',
              'Plan your first monthly budget',
              'Track debts and future goals',
            ].map((step, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold shrink-0">
                  {i + 1}
                </div>
                <span className="text-purple-100 text-sm font-medium">{step}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-blue-200 text-sm relative z-10">
          Free forever. No credit card needed. 🚀
        </p>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-12 bg-gray-50 lg:bg-white">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-10">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-extrabold text-lg shadow-md">
            K
          </div>
          <span className="text-2xl font-extrabold text-gray-900 tracking-tight">Katabo</span>
        </div>

        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h2 className="text-2xl font-extrabold text-gray-900">Create an account ✨</h2>
            <p className="text-gray-500 mt-1 text-sm">Set up your phone number and a 4-digit PIN</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-2xl text-sm font-medium animate-fade-in-up">
                ⚠️ {error}
              </div>
            )}

            {/* Phone field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  id="phone"
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full pl-11 pr-4 py-3.5 bg-gray-100 border border-transparent rounded-2xl text-sm font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                  placeholder="e.g. 0991234567"
                />
              </div>
            </div>

            {/* PIN field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Choose a 4-Digit PIN</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  id="pin"
                  type={showPin ? 'text' : 'password'}
                  maxLength="4"
                  required
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  className="w-full pl-11 pr-12 py-3.5 bg-gray-100 border border-transparent rounded-2xl text-lg font-bold text-gray-900 text-center tracking-[0.5em] placeholder-gray-400 focus:outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                  placeholder="••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPin(p => !p)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {/* PIN strength dots */}
              {pin.length > 0 && (
                <div className="flex gap-2 mt-2 justify-center">
                  {[0, 1, 2, 3].map(i => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full transition-all duration-200 ${
                        i < pin.length
                          ? pin.length === 4 ? 'bg-green-500 scale-110' : 'bg-blue-500 scale-110'
                          : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Confirm PIN field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Confirm PIN</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  id="confirmPin"
                  type={showConfirm ? 'text' : 'password'}
                  maxLength="4"
                  required
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                  className={`w-full pl-11 pr-12 py-3.5 bg-gray-100 text-lg font-bold text-gray-900 text-center tracking-[0.5em] placeholder-gray-400 focus:outline-none focus:bg-white transition-all rounded-2xl border-2 ${
                    pinMatch
                      ? 'border-green-400 bg-green-50 focus:ring-2 focus:ring-green-100'
                      : pinMismatch
                      ? 'border-red-300 bg-red-50 focus:ring-2 focus:ring-red-100'
                      : 'border-transparent focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
                  }`}
                  placeholder="••••"
                />
                {/* Show/hide toggle or match indicator */}
                <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {pinMatch && <CheckCircle className="w-4 h-4 text-green-500" />}
                  <button
                    type="button"
                    onClick={() => setShowConfirm(p => !p)}
                    className="text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {pinMismatch && (
                <p className="text-xs text-red-500 font-medium mt-1.5 ml-1">PINs do not match</p>
              )}
              {pinMatch && (
                <p className="text-xs text-green-600 font-medium mt-1.5 ml-1">✓ PINs match</p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || pinMismatch}
              className="w-full flex items-center justify-center gap-2 py-3.5 px-6 mt-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white font-bold rounded-2xl shadow-md shadow-blue-200 hover:shadow-lg hover:shadow-blue-200 hover:from-blue-700 hover:to-blue-500 active:scale-[0.98] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Creating account...
                </>
              ) : (
                <>
                  Create Account <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* Footer link */}
          <p className="mt-8 text-center text-sm text-gray-500">
            Already have an account?{' '}
            <Link to="/login" className="font-bold text-blue-600 hover:text-blue-700 hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
